import { feature } from "../common/api/Models/FeatureConfiguration";

/**
 * Checks if a feature is enabled for a specific user location.
 *
 * @param {string | number} userLocation - The user's location, either as a string or number.
 * @param {feature} feature - The feature object containing enabled locations and optionally receive types.
 * @return {boolean} True if the feature is enabled for the location, otherwise false.
 */
export function isFeatureEnabledForLocation(userLocation: string | number, feature: feature): boolean {
	return isFeatureEnabled(userLocation, feature);
}

/**
 * Checks if a feature is enabled for a specific user location and receive type.
 *
 * @param {string | number} userLocation - The user's location, either as a string or number.
 * @param {feature} feature - The feature object containing enabled locations and optionally receive types.
 * @param {string} receiveType - The receive type to check.
 * @return {boolean} True if the feature is enabled for the location and receive type, otherwise false.
 */
export function isFeatureEnabledForLocationAndReceiveType(
	userLocation: string | number,
	feature: feature,
	receiveType: string
): boolean {
	return isFeatureEnabled(userLocation, feature, receiveType);
}

export function isFeatureEnabledForLocationReceiveTypeAndTransferReason(
	userLocation: string | number,
	feature: feature,
	receiveType: string,
	transferReason?: string | null
): boolean {
	return isFeatureEnabled(userLocation, feature, receiveType, transferReason);
}

/**
 * Checks if a feature is enabled at the given user location and optionally for a receiveType.
 *
 * @param {string|number} userLocation - The location of the user, which can be either a string or a number.
 * @param {feature} feature - A feature object containing the enabled locations and receive types to check against.
 * @param {string} receiveType - The type of receive.
 * @param {string} transferReason - The reason for the transfer when it is a transfer receive type.
 * @return {boolean} True if the feature is enabled for the user's location, otherwise false.
 */
function isFeatureEnabled(
	userLocation: string | number,
	feature: feature,
	receiveType?: string | null,
	transferReason?: string | null
): boolean {
	if (!feature) {
		return false;
	}

	const location = typeof userLocation === "string" ? parseInt(userLocation, 10) : userLocation;
	const { enabledLocations = [], enabledReceiveTypes = [] } = feature;

	if (receiveType) {
		const receiveTypes = enabledReceiveTypes.map((type) => type.toLowerCase());
		let enabled = enabledLocations.includes(location) && receiveTypes.includes(receiveType.toLowerCase());

		if (transferReason) {
			const transferReasons = (feature.enabledTransferReasons ?? []).map((reason) => reason.toLowerCase());
			enabled = enabled && (transferReasons.includes("all") || transferReasons.includes(transferReason.toLowerCase()));
		}

		return enabled;
	}
	return enabledLocations.includes(location);
}
